import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { SprayCan } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import nurseryService from '@/services/nursery';
import type { LotSprayHistoryEntry } from '@/types/spray';
import { APPLICATION_METHOD_LABELS } from '@/types/spray';
import { useOrgTimezone, formatLocalDateInTz, formatDateTimeInTz } from '@/utils/timezone';
import { toNumOr } from '@/utils/numeric';

/** Spray statuses with a label, read as `status.<status>`; any other shows as it comes. */
const STATUS = new Set(['created', 'assigned', 'in_progress', 'paused', 'completed']);

/**
 * What this lot has been sprayed with, newest first: the products and rates,
 * when, and whether the lot was on the spray as planned or as sprayed. Read
 * from the spray module's record of the lots each nursery spray landed on.
 */
function LotSprayHistory({ lotId, canOpenSprays }: { lotId: number; canOpenSprays: boolean }) {
  const { t } = useTranslation('nursery', { keyPrefix: 'LotSprayHistory' });
  const tz = useOrgTimezone();
  const [entries, setEntries] = useState<LotSprayHistoryEntry[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    nurseryService.getLotSprays(lotId)
      .then(res => {
        if (cancelled) return;
        if (res.success) setEntries(res.data ?? []);
        else setError(t('readFailed'));
      })
      .catch(() => { if (!cancelled) setError(t('readFailed')); });
    return () => { cancelled = true; };
  }, [lotId, t]);

  return (
    <div className="bg-white border border-neutral-200 rounded-xl p-5">
      <div className="flex items-center gap-2 mb-3">
        <SprayCan className="h-5 w-5 text-primary-600" />
        <h3 className="text-sm font-semibold text-neutral-800">{t('title')}</h3>
      </div>
      {error ? (
        <p className="text-sm text-red-600">{error}</p>
      ) : entries == null ? (
        <p className="text-sm text-neutral-400">{t('loading')}</p>
      ) : entries.length === 0 ? (
        <p className="text-sm text-neutral-400">{t('none')}</p>
      ) : (
        <ul className="divide-y divide-neutral-100">
          {entries.map(e => {
            const when = e.completed_at
              ? formatDateTimeInTz(e.completed_at, tz)
              : formatLocalDateInTz(e.scheduled_date, tz);
            const title = e.reference_number ? `${e.reference_number} · ${e.title}` : e.title;
            return (
              <li key={e.id} className="py-3 space-y-1">
                <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5">
                  {canOpenSprays ? (
                    <Link to={`/spray/${e.id}`} className="min-w-0 break-words text-sm font-medium text-primary-700 hover:underline">
                      {title}
                    </Link>
                  ) : (
                    <span className="min-w-0 break-words text-sm font-medium text-neutral-800">{title}</span>
                  )}
                  <span className="text-xs text-neutral-500">{STATUS.has(e.status) ? t(`status.${e.status}`) : e.status} · {when}</span>
                </div>
                <ul className="text-sm text-neutral-700">
                  {e.chemicals.map((c, i) => (
                    <li key={i} className="break-words">
                      {c.product_name}
                      {c.rate != null && (
                        <span className="text-neutral-500"> · {toNumOr(c.rate) * toNumOr(c.concentration_factor, 1)} {c.rate_unit}</span>
                      )}
                      {c.target_pest && <span className="text-neutral-400"> · {c.target_pest}</span>}
                      {c.rei_hours != null && <span className="text-neutral-400"> · {t('reEntry', { hours: c.rei_hours })}</span>}
                    </li>
                  ))}
                </ul>
                <p className="text-xs text-neutral-500">
                  {[
                    e.application_method ? t(`applicationMethod.${e.application_method}`, { defaultValue: APPLICATION_METHOD_LABELS[e.application_method] }) : null,
                    e.basis === 'sprayed'
                      ? t('treesSprayed', { n: Number(e.plant_count).toLocaleString() })
                      : t('treesPlanned', { n: Number(e.plant_count).toLocaleString() }),
                    e.location_name,
                  ].filter(Boolean).join(' · ')}
                </p>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

export default LotSprayHistory;
